import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../components/Logo';
import { api, apiPost, refreshApiBaseDetection, isOnDemoSite } from '../lib/api.js';
import SEO from '../components/SEO';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [humanToken, setHumanToken] = useState('');
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const requireHuman = (import.meta.env.VITE_REQUIRE_TURNSTILE || '').toString().toLowerCase() === 'true';
  const [showPassword, setShowPassword] = useState(false);
  const appearance = import.meta.env.VITE_TURNSTILE_APPEARANCE || 'interaction-only';
  const turnstileSize = import.meta.env.VITE_TURNSTILE_SIZE || 'normal';
  const widgetIdRef = useRef(null);
  const [redirecting, setRedirecting] = useState(false);
  const isDemo = isOnDemoSite();

  // Compute a theme-aware gradient using CSS variables and oklab color-mix
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  
  // If already authenticated, skip login and go to dashboard (or intended redirect)
  // Uses useLayoutEffect for synchronous check before paint
  // BUT: if there's a redirect param, user was sent here from a protected route,
  // meaning their token was likely just invalidated - don't auto-redirect
  useLayoutEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const redirect = params.get('redirect');
      
      // If there's a redirect param, user was sent here due to auth failure
      // Don't auto-redirect - let them login again
      if (redirect) {
        // Clear potentially stale token to prevent loops
        try { localStorage.removeItem('auth:token'); } catch (e) { void e; }
        return;
      }
      
      const token = localStorage.getItem('auth:token');
      if (token) {
        setRedirecting(true);
        navigate('/dashboard', { replace: true });
      }
    } catch (e) { void e; }
  }, [navigate]);

  // Load and render Cloudflare Turnstile widget
  useEffect(() => {
    if (!siteKey) return;
    const onloadName = 'onTurnstileLoad';
    const ensureRender = () => {
      try {
        const container = document.getElementById('turnstile-container');
        if (!window.turnstile || !container) return;
        if (widgetIdRef.current) {
          try { window.turnstile.reset(widgetIdRef.current, { theme: isDark ? 'dark' : 'light' }); } catch (e) { void e; }
          return;
        }
        const id = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: isDark ? 'dark' : 'light',
          appearance,
          size: turnstileSize,
          callback: (token) => setHumanToken(token),
          'error-callback': () => setHumanToken(''),
          'timeout-callback': () => setHumanToken(''),
        });
        widgetIdRef.current = id;
      } catch (e) { void e; }
    };
    if (window.turnstile) { ensureRender(); return; }
    window[onloadName] = () => ensureRender();
    const existing = document.querySelector('script[data-turnstile]');
    if (!existing) {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=' + onloadName;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-turnstile', '1');
      document.head.appendChild(s);
    }
    return () => {
      try { if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current); } catch (e) { void e; }
      widgetIdRef.current = null;
    };
  }, [siteKey, isDark, appearance, turnstileSize]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Only enforce human verification when explicitly required
      if (requireHuman && siteKey && !humanToken) {
        throw new Error('Please complete the human verification.');
      }
      const data = await apiPost('/users/login', { email, password, turnstileToken: humanToken });
      const token = data?.token || data?.data?.token;
      if (!token) throw new Error('No token returned');

      try {
        localStorage.setItem('auth:token', token);
        // Dispatch storage event manually for same-window listeners (CurrencyContext)
        window.dispatchEvent(new StorageEvent('storage', { key: 'auth:token', newValue: token }));
        if (remember) localStorage.setItem('auth:remember', '1');
        else localStorage.removeItem('auth:remember');
      } catch (e) { void e; }

      try { if (api?.defaults) api.defaults.headers.common['Authorization'] = `Bearer ${token}`; } catch (e) { void e; }

      // Ensure API base matches the environment that validates this token
      try { await refreshApiBaseDetection(); } catch (e) { void e; }

      let user = null;
      try {
        const meRes = await api.get('/users/me', { headers: { Authorization: `Bearer ${token}` } });
        const m = meRes?.data || meRes;
        user = m?.data?.user || m?.user || m?.data || null;
      } catch {
        user = { email, display_name: email?.split('@')[0] || 'User' };
      }
      try { 
        localStorage.setItem('auth:user', JSON.stringify(user));
        // Dispatch storage event manually for same-window listeners (CurrencyContext)
        window.dispatchEvent(new StorageEvent('storage', { key: 'auth:user', newValue: JSON.stringify(user) }));
      } catch (e) { void e; }

      try {
        const params = new URLSearchParams(window.location.search || '');
        const redirect = params.get('redirect');
        if (redirect) navigate(decodeURIComponent(redirect));
        else navigate('/dashboard');
      } catch {
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Login failed. Please try again.';
      setError(msg);
      // Reset Turnstile widget after failed attempt to get fresh token
      try {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
          setHumanToken('');
        }
      } catch (e) { void e; }
    } finally {
      setLoading(false);
    }
  };

  // Suppress UI while redirecting
  if (redirecting) return null;

  return (
    <>
      <SEO page="login" />
      <div className="min-h-screen w-full grid grid-cols-1 md:grid-cols-10 items-stretch">
      {/* Left hero with centered large logo; no background color */}
      <div
        className="flex items-center justify-center md:col-span-7 w-full h-full px-8 md:px-12 py-8 bg-transparent"
      >
        <div className="w-full flex items-center justify-center">
          <Logo className="h-72 md:h-80 w-auto" alt="Kreatives" variant="footer" />
        </div>
      </div>

      {/* Right: form aligned right without divider, edge-to-edge */}
      <div className="flex items-center justify-center md:justify-end md:col-span-3">
        <div className="w-full bg-transparent px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-md mx-auto md:mx-0 md:ml-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-primary-text">
              {t('auth.login.title')}
            </h1>
            <p className="mt-2 text-sm text-secondary-text">
              {t('auth.login.subtitle')}{' '}
              <Link to="/register" className="text-brand-600 dark:text-brand-400 hover:underline hover:text-brand-700 dark:hover:text-brand-300">
                {t('auth.login.signUp')}
              </Link>
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-1">
                {t('auth.login.email')}
              </label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2 6.5A2.5 2.5 0 0 1 4.5 4h15A2.5 2.5 0 0 1 22 6.5v11A2.5 2.5 0 0 1 19.5 20h-15A2.5 2.5 0 0 1 2 17.5v-11Zm2.5-.5a.5.5 0 0 0-.5.5v.41l8 4.59 8-4.59V6.5a.5.5 0 0 0-.5-.5h-15Zm15.5 3.76-7.57 4.35a1.5 1.5 0 0 1-1.46 0L3 9.76V17.5c0 .28.22.5.5.5h15a.5.5 0 0 0 .5-.5V9.76Z"/>
                </svg>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-4 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-primary-text mb-1">
                {t('auth.login.password')}
              </label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2a6 6 0 0 0-6 6v2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V8a6 6 0 0 0-6-6Zm-4 8V8a4 4 0 1 1 8 0v2H8Zm-3 2h14v8H5v-8Z"/>
                </svg>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-10 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-text hover:text-primary-text"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7Zm0 12c-2.76 0-5-2.24-5-5 0-.62.12-1.21.33-1.75l6.42 6.42c-.54.21-1.13.33-1.75.33Zm4.67-2.25-6.42-6.42C10.79 8.12 11.38 8 12 8c2.76 0 5 2.24 5 5 0 .62-.12 1.21-.33 1.75Z"/>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {siteKey && (
              <div className="pt-2">
                <div id="turnstile-container" className="inline-block" />
              </div>
            )}

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-secondary-text">
                <input
                  type="checkbox"
                  className="accent-brand-500"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                {t('auth.login.rememberMe')}
              </label>
              <Link id="forgot-link" to="/forgot-password" className="text-sm hover:underline">
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            {error && (
              <div className="text-sm text-red-500" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="cta-brand w-full py-3"
              data-variant="primary"
              disabled={loading || (!!siteKey && !humanToken)}
            >
              {loading ? '…' : t('auth.login.signIn')}
            </button>
          </form>
        </div>
      </div>
      </div>
    </>
  );
}
